import { 
    useState, 
    useLayoutEffect,
    useEffect,
    useReducer,
    useMemo,
    useRef,
    useCallback,
    forwardRef,
    StrictMode,
    createContext,
    useContext
} from 'react';

import { createRoot } from 'react-dom/client'; 

import { 
    SearchControl,
    Popover,
    MenuGroup,
    MenuItem,
    Button,
    Flex, 
    FlexBlock, 
    FlexItem,
    TextControl,
    BaseControl,
    __experimentalText as Text,
    __experimentalVStack as VStack
} from '@wordpress/components';

import { __ } from '@wordpress/i18n';

import { 
    Map,
    Marker,
    getAddressFromComponents,
    filterAddressesByTypes,
    setAddressFromMarker
} from './map.jsx';

const context = createContext( {} )


/**
 * Checks whether the text contains coordinates.
 * @param {string} text - Comma separated coordinates.
 * @returns boolean
 */
export function getisCoordinates(text = '') {
    if ( !text || typeof text !== 'string' )
        return false;

    let [lat, lng] = text.split( ',' )
    lat = parseFloat( lat )
    lng = parseFloat( lng )

    return !Number.isNaN( lat ) && !Number.isNaN( lng );
}



const AddressInput = ( {
    country_code
} ) => {
    const ref = useRef()
    const [ search, setSearch ] = useState( '' )
    const [ suggestions, setSuggestions ] = useState( [] )
    const [ coordinates, setCoordinates ] = useState( {} )
    const [ open, setOpen ] = useState( false )

    const isCoordinates = useMemo( (search) => getisCoordinates( search ), [search] )


    const {
        services: {
            autocomplete,
            places,
            geocoder
        },
        place,
        setPlace,
        address,
        setAddress,
        options
    } = useContext( context )


    function getPlaceFromId(placeId) {
        if ( placeId ) {
            places.getDetails( {
                placeId,
                fields: [
                    'place_id',
                    'address_components', 
                    'formatted_address', 
                    'geometry',
                    'name'
                ]
            }, ( placeObj, status ) => {
                if ( status == 'OK' ) {
                    setPlace( placeObj )
                }
            } )
        }// else {
            //setPlace( {} )
        //}
    }


    const checkCoordinates = (value) => {
        let [lat, lng] = value.split( ',' )

        //console.log(lat, lng)

        if ( !getisCoordinates( value ) ) {
            setCoordinates( {} )
            setSearch( value )
        } else {
            if ( !!options?.allowCoordinates ) {
                lat = parseFloat( lat )
                lng = parseFloat( lng )

                setCoordinates( {
                    lat,
                    lng
                } )
                setSearch( [lat, lng].toString() )
            }
        }
    }


    const setFormattedAddress = (place) => {
        setAddress( {
            address: getAddressFromComponents( place.address_components, place.geometry.location, options ),
            updateCoordinates: true
        } )
    }


    const displaySuggestions = useMemo( () => {
        return suggestions.filter( suggestion => suggestion.place_id !== place?.place_id )
    }, [suggestions] )


    useEffect( () => {
        if ( !!autocomplete && !isCoordinates && !Object.keys( coordinates ).length ) {
            if ( search.length >= 3 ) {
                autocomplete.getPlacePredictions( {
                    input: search,
                    types: ['address'],
                    componentRestrictions: {
                        country: country_code
                    }
                }, ( predictions, status ) => {       
                    if ( !!predictions?.length && status == 'OK' ) {
                        setSuggestions( predictions )
                    }
                } )
            } else {
                setSuggestions( [] )
                //setPlaceId( '' )

                if ( place && !!Object.keys( place )?.length ) {
                    setPlace( {} )
                }
            }
        }
    }, [
        search,
        autocomplete,
        place
    ] )


    useEffect( () => {
        if ( !!geocoder ) {
            if ( !!Object.keys( coordinates ).length ) {
                geocoder.geocode( {
                    location: coordinates,
                    region: country_code,
                    //componentRestrictions: {
                        //country: country_code
                    //},
                    //result_type: 'administrative_area_level_1|administrative_area_level_2|administrative_area_level_3|administrative_area_level_4|administrative_area_level_5|sublocality|sublocality_level_1|sublocality_level_2|sublocality_level_3|sublocality_level_4|street_address|route',
                    //location_type: 'RANGE_INTERPOLATED'
                } )
                .then( ( response ) => {
                    if ( !!response?.results.length ) {
                        const filteredAddresses = filterAddressesByTypes( response.results, [
                            'street_address',
                            'route'
                        ] )
                        setSuggestions( filteredAddresses )

                        //console.log(response.results)
                    } else {
                        console.log( 'No results found' )
                    }
                } )
            } else {
                setSuggestions( [] )
            }
        }
    }, [
        coordinates,
        geocoder,
        //place
    ] )


    useEffect( () => {
        if ( place === null ) {
            setSearch( '' )
            setSuggestions( [] )
            setCoordinates( [] )
            //setPlaceId( '' )
        }
    }, [place] )


    return (
        <div className="fma-search-control-wrapper">
            <SearchControl
                placeholder={ __( 'Search address' ) }
                value={ search }
                onChange={ checkCoordinates }
                onFocus={ () => setOpen( true ) }
            />

            { open && !!displaySuggestions.length &&
                <span
                    ref={ ref }
                    className="fma-search-control-popover-anchor"
                >
                    <Popover
                        anchor={ ref.current }
                        focusOnMount={ false }
                        noArrow={ false }
                        offset={ 10 }
                    >
                        <MenuGroup className="fma-suggestions">
                            { displaySuggestions.map( item => 
                                <MenuItem
                                    className="fma-suggestion"
                                    isPressed={ place?.place_id == item.place_id }
                                    onClick={ () => {
                                        setSuggestions( [] )
                                        setOpen( false )

                                        //console.log(search)

                                        if ( getisCoordinates( search ) ) {
                                            setFormattedAddress( item )
                                            setPlace( item )
                                        } else {
                                            getPlaceFromId( item.place_id )
                                        }

                                        setSearch( item?.description ?? item?.formatted_address ?? '' ) 
                                    } }
                                >
                                    <Text 
                                        isBlock
                                        highlightWords={ isCoordinates ? [] : [search] }
                                    >
                                        { item?.description ?? item?.formatted_address ?? '' }
                                    </Text>
                                </MenuItem>
                            ) }
                        </MenuGroup>
                    </Popover>
                </span>
            }
        </div>
    );
}


const MetaFields = ( {
    defaultAddress,
    toggleInputType,
    inputType
} ) => {
    const {
        address,
        setAddress,
        place,
        setPlace,
        options
    } = useContext( context )

    const setFormattedAddress = (place) => {
        setAddress( {
            address: getAddressFromComponents( place?.address_components, place?.geometry?.location, options ),
            updateCoordinates: true
        } )
    }


    const getLatLngString = (latLngObj) => {
        if ( !latLngObj )
            return '';
        
        return Object.values( latLngObj ).toString()
    }


    useEffect( () => {
        if ( !!place ) {
            setFormattedAddress( place )
        }
    }, [place] )


    return (
        <div
            className="fma-meta-fields"
        >
            <BaseControl 
                label={ __( 'Country' ) }
                className="fma-meta-field"
                __nextHasNoMarginBottom
            >
                <input
                    type="text"
                    name="fma_product_country"
                    value={ address.country }
                    readOnly
                />
            </BaseControl>

            <BaseControl 
                label={ __( 'Postal code' ) }
                className="fma-meta-field"
                __nextHasNoMarginBottom
            >
                <input
                    type="text"
                    name="fma_product_postal_code"
                    value={ address.postal_code }
                    readOnly
                    size="8"
                />
            </BaseControl>

            <BaseControl 
                label={ __( 'State' ) }
                className="fma-meta-field"
                __nextHasNoMarginBottom
            >
                <Flex gap={ 1 }>
                    <input
                        type="text"
                        name="fma_product_region"
                        value={ address.region }
                        readOnly
                    />

                    <input
                        type="text"
                        name="fma_product_region_code"
                        value={ address.region_code }
                        readOnly
                        size="3"
                    />
                </Flex>
            </BaseControl>

            <BaseControl 
                label={ __( 'City' ) }
                className="fma-meta-field"
                __nextHasNoMarginBottom
            >
                <input
                    type="text"
                    name="fma_product_city"
                    value={ address.city }
                    readOnly
                />
            </BaseControl>

            <BaseControl 
                label={ __( 'Street' ) }
                className="fma-meta-field"
                __nextHasNoMarginBottom
            >
                <input
                    type="text"
                    name="fma_product_street"
                    value={ address.street }
                    readOnly
                />
            </BaseControl>

            { !!options?.displayCoordinates &&
                <BaseControl 
                    label={ __( 'Coordinates' ) }
                    className="fma-meta-field"
                    __nextHasNoMarginBottom
                >
                    <input
                        type="text"
                        name="fma_product_lat_lng"
                        value={ getLatLngString( address.latLng ) }
                        readOnly
                        required
                    />
                </BaseControl>
            }

            {/*<Button
                variant="secondary"
                icon="location-alt"
                //disabled={ getLatLngString( defaultAddress.latLng ) === getLatLngString( address.latLng ) }
                onClick={ () => {
                    toggleInputType( true ) 
                    //setPlace( null )
                } }
            />*/}

            { ( getLatLngString( defaultAddress.latLng ) !== getLatLngString( address.latLng ) ) &&
                <Button
                    variant="tertiary"
                    disabled={ getLatLngString( defaultAddress.latLng ) === getLatLngString( address.latLng ) }
                    onClick={ () => {
                        setAddress( {
                            address: defaultAddress,
                            updateCoordinates: true
                        } ) 
                        setPlace( null )
                    } }
                >
                    { __( 'Restore' ) }
                </Button>
            }
        </div>
    )
}


const MapContainer = ( {
    country_code,
    country,
    postal_code = '',
    region = '',
    region_code = '',
    city = '',
    street = '',
    lat_lng,
    options
} ) => {
    const defaultAddress = {
        postal_code,
        country,
        region,
        region_code,
        city,
        street,
        latLng: lat_lng
    }

    const ref = useRef()
    const [map, setMap] = useState()
    const [services, setServices] = useState( {
        autocomplete: null,
        places: null,
        geocoder: null,

    } )
    const [place, setPlace] = useState( null )
    const [address, setAddress] = useReducer( (state, action) => {
        return {
            ...action.address,
            country: defaultAddress.country, // always on woocommerce location
            latLng: action.updateCoordinates ? action.address.latLng : state.latLng // update address condition
        }
    }, {
        ...defaultAddress,
        country_code
    } )


    function createMarkerSuggestions(coordinates) {
        return services.geocoder.geocode( {
            location: coordinates,
            region: country_code
        } )
    }


    useEffect( () => {
        if ( ref.current && !map && !!google?.maps?.places ) {
            const { 
                Map,
                Geocoder,
                event,
                places: {
                    AutocompleteService,
                    PlacesService
                }
            } = google.maps

            const mapObj = new Map( ref.current, {
                zoom: options.defaultZoom,
                mapTypeControl: false
            } )
            
            setServices( {
                autocomplete: new AutocompleteService(),
                places: new PlacesService( mapObj ),
                geocoder: new Geocoder(),
                events: event
            } )

            setMap( mapObj )
        }
    }, [ref] )


    return (
        <context.Provider
            value={ {
                services,
                address,
                setAddress,
                place,
                setPlace,
                options
            } }
        >
            <MetaFields
                defaultAddress={ defaultAddress }
            />

            <div className="fma-search-control">
                <AddressInput country_code={ country_code } />
            </div>

            <div 
                ref={ ref } 
                className="fma-maps-wrapper"
                style={ {
                    display: !!options?.displayMap ? 'block' : 'none'
                } }
            >
                { !!options?.displayMap && !!map &&
                    <Map
                        map={ map }
                        place={ place }
                        center={ address?.latLng ?? {
                            lat: parseFloat( options.defaultCenter.lat ),
                            lng: parseFloat( options.defaultCenter.lng )
                        } }
                        zoom={ !!address?.latLng ? options.zoom : options.defaultZoom }
                        initZoom={ options.defaultZoom }
                        hasAddress={ !!address?.latLng }
                    >
                        { !!address?.region && 
                            <Marker 
                                position={ address.latLng } 
                                map={ map }
                                title={ place?.name }
                                draggable
                                getInfoContent={ createMarkerSuggestions }
                                setAddress={ () => setAddressFromMarker( services.events, ref.current, address, setAddress, options ) }
                            />
                        }
                    </Map>
                }
            </div>
        </context.Provider>
    )
}
 

function init() {
    const container = document.getElementById( 'fma_core-map-container' ) 


    if ( container && window?.google ) { 
        const root = createRoot( container )

        root.render( 
            <StrictMode>
                <MapContainer
                    { ...JSON.parse( container.dataset.address ) }
                    options={ JSON.parse( container.dataset.options ) }
                />
            </StrictMode> 
        )
    }
}


document.addEventListener( 'DOMContentLoaded', ( { target } ) => {
    init( target.body ) 
} )